import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { getSsoLoginRedirectUrl, isSsoEnforced, SSO_RETURN_PATH_KEY, TOPIC_NAV_SKIP_AUTH_PROFILE_KEY } from "@/config/sso";
import { clearSsoClientSession, getStoredAccessToken, getUserDetails, hasSsoSessionReadySync } from "@/services/ssoApi";
import { baseUrl } from "@/config/api";

function redirectToSsoLogin(pathWithSearch: string): () => void {
  if (SSO_RETURN_PATH_KEY) {
    sessionStorage.setItem(SSO_RETURN_PATH_KEY, pathWithSearch);
  }
  const t = window.setTimeout(() => {
    window.location.href = getSsoLoginRedirectUrl();
  }, 500);
  return () => clearTimeout(t);
}

/** SSO: show loading only when token exists but session cache is not ready yet (backend primes cache on token exchange). */
export default function RequireAuth() {
  const [phase, setPhase] = useState<"loading" | "ok" | "misconfigured">(() => {
    if (!isSsoEnforced()) return "ok";
    if (!baseUrl) return "misconfigured";
    return hasSsoSessionReadySync(getStoredAccessToken()) ? "ok" : "loading";
  });

  useEffect(() => {
    if (!isSsoEnforced()) {
      setPhase("ok");
      return;
    }

    if (!baseUrl) {
      setPhase("misconfigured");
      return;
    }

    const pathWithSearch = window.location.pathname + window.location.search;
    const token = getStoredAccessToken();

    const skipProfileOnce =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(TOPIC_NAV_SKIP_AUTH_PROFILE_KEY) === "1";

    if (skipProfileOnce) {
      sessionStorage.removeItem(TOPIC_NAV_SKIP_AUTH_PROFILE_KEY);
      if (!token) {
        return redirectToSsoLogin(pathWithSearch);
      }
      setPhase("ok");
      return;
    }

    if (!token) {
      return redirectToSsoLogin(pathWithSearch);
    }

    if (hasSsoSessionReadySync(token)) {
      setPhase("ok");
      return;
    }

    let cancelled = false;
    let clearRedirect: (() => void) | undefined;

    (async () => {
      const userInfo = await getUserDetails(token);
      if (cancelled) return;

      const invalidToken =
        userInfo && typeof userInfo === "object" && "error" in userInfo && userInfo.error === "invalid_token";

      const empty =
        !userInfo ||
        (typeof userInfo === "object" &&
          !Array.isArray(userInfo) &&
          !("error" in userInfo) &&
          Object.keys(userInfo).length === 0);

      if (invalidToken || empty) {
        clearSsoClientSession();
        clearRedirect = redirectToSsoLogin(pathWithSearch);
        return;
      }

      if (cancelled) return;
      setPhase("ok");
    })();

    return () => {
      cancelled = true;
      clearRedirect?.();
    };
  }, []);

  if (!isSsoEnforced()) {
    return <Outlet />;
  }

  if (phase === "misconfigured") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 text-center">
        <h1 className="mb-2 text-lg font-semibold text-slate-800">Configuration error</h1>
        <p className="max-w-md text-sm text-slate-600">
          SSO is enabled but <code className="rounded bg-slate-200 px-1">VITE_API_BASE_URL</code> is missing. Set it
          in <code className="rounded bg-slate-200 px-1">.env</code> and rebuild the app.
        </p>
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-600">
        Checking session…
      </div>
    );
  }

  return <Outlet />;
}
